import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { ActionModuleKind } from '@/api/model'

interface Props {
  open: boolean
  /** Source is a template → offer a live module; otherwise offer the template kinds. */
  isTemplate: boolean
  isSuperAdmin: boolean
  isSaving: boolean
  onClose: () => void
  /** Called with the kind of module to create from the current one. */
  onConfirm: (kind: ActionModuleKind) => void
}

/** Confirms creating a template from a live module, or a live module from a template. */
export function CopyTemplateDialog({ open, isTemplate, isSuperAdmin, isSaving, onClose, onConfirm }: Props) {
  const { t } = useTranslation(['action-modules', 'common'])

  const confirm = (kind: ActionModuleKind) => {
    onClose()
    onConfirm(kind)
  }

  const description = isTemplate
    ? t('actionModuleTemplate.CopyFromTemplateConfirmDescription')
    : isSuperAdmin
      ? t('actionModuleTemplate.CopyFromLiveSuperAdminConfirmDescription')
      : t('actionModuleTemplate.CopyFromLiveConfirmDescription')

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={!isTemplate && isSuperAdmin ? 'sm:max-w-xl' : undefined}>
        <DialogHeader>
          <DialogTitle>
            {isTemplate
              ? t('actionModuleTemplate.CopyFromTemplateConfirmTitle')
              : t('actionModuleTemplate.CopyFromLiveConfirmTitle')}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={isSaving} onClick={onClose}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          {isTemplate ? (
            <Button disabled={isSaving} onClick={() => confirm(ActionModuleKind.Live)}>
              {t('actionModuleTemplate.CreateLiveModule')}
            </Button>
          ) : (
            <>
              <Button disabled={isSaving} onClick={() => confirm(ActionModuleKind.CompanyTemplate)}>
                {t('actionModuleTemplate.CreateCompanyTemplate')}
              </Button>
              {isSuperAdmin && (
                <Button disabled={isSaving} onClick={() => confirm(ActionModuleKind.GlobalTemplate)}>
                  {t('actionModuleTemplate.CreateGlobalTemplate')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
